import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Check,
  CreditCard,
  Mail,
  MapPin,
  Phone,
  StickyNote,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { ErrorCard } from "@/components/shared/ErrorCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  useBooking,
  useMarkBookingPaid,
  useUpdateBookingStatus,
  computeBookingTotal,
  BookingStatus,
} from "@/hooks/useBookings";
import { formatDate, formatCurrency } from "@/utils/format";

const InfoRow = ({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value?: string | number | null;
}) => (
  <div className="flex items-start gap-3 text-sm">
    <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-foreground truncate">
        {value || value === 0 ? value : "—"}
      </div>
    </div>
  </div>
);

export const BookingShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: booking, isLoading, error, refetch } = useBooking(id);
  const updateStatus = useUpdateBookingStatus();
  const markPaid = useMarkBookingPaid();

  const total = booking ? computeBookingTotal(booking) : 0;
  const paid = (booking?.payments ?? [])
    .filter((p) => p.status === "PAID")
    .reduce((sum, p) => sum + Number(p.amountTotal || 0), 0);
  const due = Math.max(0, total - paid);
  const isPending = booking?.status === BookingStatus.PENDING;
  const canMarkPaid =
    !!booking &&
    paid === 0 &&
    total > 0 &&
    booking.status !== BookingStatus.CANCELLED &&
    booking.status !== BookingStatus.REJECTED;
  const canComplete =
    booking?.status === BookingStatus.CONFIRMED ||
    booking?.status === BookingStatus.ACTIVE;

  const handleAccept = () => {
    if (!booking) return;
    updateStatus.mutate(
      { id: booking.id, status: BookingStatus.CONFIRMED },
      {
        onSuccess: () => toast.success("Booking accepted"),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const handleReject = () => {
    if (!booking) return;
    const reason =
      window.prompt("Reason for rejection (optional)") ?? undefined;
    updateStatus.mutate(
      { id: booking.id, status: BookingStatus.REJECTED, statusReason: reason },
      {
        onSuccess: () => toast.success("Booking rejected"),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const handleMarkPaid = () => {
    if (!booking) return;
    if (
      !confirm(
        `Mark booking ${booking.bookingRef} as paid (${formatCurrency(total)})?`,
      )
    )
      return;
    markPaid.mutate(
      { id: booking.id },
      {
        onSuccess: () => toast.success("Marked as paid"),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const handleComplete = () => {
    if (!booking) return;
    updateStatus.mutate(
      { id: booking.id, status: BookingStatus.COMPLETED },
      {
        onSuccess: () => toast.success("Booking completed"),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const busy = updateStatus.isPending || markPaid.isPending;

  return (
    <AppLayout title="Booking detail">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : error ? (
        <ErrorCard
          message={(error as Error).message}
          onRetry={() => refetch()}
        />
      ) : !booking ? (
        <EmptyState title="Booking not found" />
      ) : (
        <>
          <div className="bg-card rounded-card border border-border p-5 mb-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="font-mono text-xs text-muted-foreground">
                {booking.bookingRef}
              </div>
              <h2 className="text-xl font-semibold">
                {booking.business?.nameEn ?? "—"}
              </h2>
              <p className="text-sm text-muted-foreground">
                Created {formatDate(booking.createdAt)} ·{" "}
                {booking.durationNights} nights · {booking.careType}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={booking.status} />
              {isPending && (
                <>
                  <Button onClick={handleAccept} disabled={busy}>
                    <Check className="w-4 h-4 mr-1.5" /> Accept
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleReject}
                    disabled={busy}
                  >
                    <X className="w-4 h-4 mr-1.5" /> Reject
                  </Button>
                </>
              )}
              {canMarkPaid && (
                <Button
                  variant="outline"
                  onClick={handleMarkPaid}
                  disabled={busy}
                >
                  <CreditCard className="w-4 h-4 mr-1.5" /> Mark as paid
                </Button>
              )}
              {canComplete && paid > 0 && (
                <Button
                  variant="outline"
                  onClick={handleComplete}
                  disabled={busy}
                >
                  <Check className="w-4 h-4 mr-1.5" /> Mark completed
                </Button>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="bg-card rounded-card border border-border p-5 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <User className="w-4 h-4" /> Customer
              </h3>
              <InfoRow
                icon={User}
                label="Name"
                value={booking.customer?.name}
              />
              <InfoRow
                icon={Phone}
                label="Phone"
                value={booking.customer?.user?.phone}
              />
              <InfoRow
                icon={Mail}
                label="Email"
                value={booking.customer?.user?.email}
              />
            </div>

            <div className="bg-card rounded-card border border-border p-5 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Centre
              </h3>
              <InfoRow
                icon={Building2}
                label="Name"
                value={booking.business?.nameEn}
              />
              <InfoRow
                icon={MapPin}
                label="City"
                value={booking.business?.city}
              />
              <InfoRow
                icon={Phone}
                label="Phone"
                value={booking.business?.phone}
              />
              <InfoRow
                icon={MapPin}
                label="Address"
                value={booking.business?.address}
              />
            </div>

            <div className="bg-card rounded-card border border-border p-5 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <CalendarDays className="w-4 h-4" /> Stay
              </h3>
              <InfoRow
                icon={CalendarDays}
                label="Check-in"
                value={formatDate(booking.checkinDate)}
              />
              <InfoRow
                icon={CalendarDays}
                label="Check-out"
                value={formatDate(booking.checkoutDate)}
              />
              <InfoRow
                icon={CalendarDays}
                label="Duration"
                value={`${booking.durationNights} nights`}
              />
              <InfoRow
                icon={User}
                label="Guests"
                value={booking.guestCount ?? 1}
              />
              <InfoRow
                icon={Building2}
                label="Room"
                value={booking.roomType?.name}
              />
              {booking.package?.name && (
                <InfoRow
                  icon={Building2}
                  label="Package"
                  value={booking.package.name}
                />
              )}
              {booking.specialRequests && (
                <InfoRow
                  icon={StickyNote}
                  label="Special requests"
                  value={booking.specialRequests}
                />
              )}
            </div>

            <div className="bg-card rounded-card border border-border p-5 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Payment
              </h3>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total</span>
                <span className="font-medium">{formatCurrency(total)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Paid</span>
                <span className="font-medium">{formatCurrency(paid)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Due</span>
                <span className="font-medium">{formatCurrency(due)}</span>
              </div>
              {(booking.payments?.length ?? 0) > 0 && (
                <div className="pt-2 border-t border-border space-y-2">
                  {booking.payments!.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between text-xs"
                    >
                      <span className="text-muted-foreground">
                        {p.createdAt ? formatDate(p.createdAt) : "—"}
                      </span>
                      <span className="font-mono">
                        {formatCurrency(Number(p.amountTotal))}
                      </span>
                      <StatusBadge status={p.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
};

export default BookingShow;
